import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import PageHeader from '@/components/PageHeader';
import { AdminPageSections, AutoRefreshToolbar, CollapsibleCard } from '@/components/common';
import UpgradePlanSection from '@/components/UpgradePlanSection';
import { AttentionCollapsibleSection } from '@/components/activity/AttentionPanel';
import { RecentActivityCollapsibleSection } from '@/components/activity/RecentActivityWidget';
import { MetricChip } from '@/components/ui';
import { useMe } from '@/context/MeContext';
import { useAutoRefreshPolling } from '@/hooks/useAutoRefreshPolling';
import type { BillingStatus, DashboardSummary } from '@/types';
import type { OrgUsageSummary } from '@/api/services/billingAdmin';
import { entitlementStatusMessage } from '@/utils/planWarning';
import { canViewWorkspaceActivity } from '@/utils/navCapabilities';
import { formatPlanDisplayName } from '@/utils/planDisplayName';
import { quotaBypassBannerText } from '@/utils/capabilities';

export default function CustomerDashboard() {
  const { me } = useMe();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [usage, setUsage] = useState<OrgUsageSummary | null>(null);
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const [s, u, b] = await Promise.all([
      api.dashboard.summary(),
      api.usage.summary().catch((e) => {
        setUsageError(e instanceof Error ? e.message : 'Usage is not available yet.');
        return null;
      }),
      api.billing.status().catch(() => null),
    ]);
    setSummary(s);
    setUsage(u);
    if (u) setUsageError(null);
    setBilling(b);
    setError('');
  }, []);

  const { autoRefresh, initialLoading } = useAutoRefreshPolling({
    hasData: () => summary !== null,
    load: async () => {
      try {
        await load();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load dashboard');
        throw e;
      }
    },
    autoRefresh: {
      intervalMs: 30000,
      storageKey: 'customer-dashboard-auto-refresh',
    },
  });

  const cards = summary?.cards;
  const statusMsg = usage
    ? entitlementStatusMessage(usage, {
        canActOnSearchQuota: me?.permissions.canCreateSearch ?? false,
      })
    : null;
  const planLabel = usage?.unlimited
    ? 'Internal (Unlimited)'
    : formatPlanDisplayName(usage?.planSlug ?? me?.plan);
  const canViewActivity = canViewWorkspaceActivity(me);
  const quotaBypassNotice = quotaBypassBannerText(me);

  return (
    <div className="dashboard-page customer-dashboard">
      <PageHeader
        title="Dashboard"
        subtitle="Your searches, matches, notifications, and plan usage"
      >
        <Link to="/searches" className="btn btn-ghost">
          Searches
        </Link>
        <Link to="/usage" className="btn btn-ghost">
          Usage
        </Link>
        <AutoRefreshToolbar
          control={autoRefresh}
          intervalOptions={[
            { value: 15000, label: 'Every 15s' },
            { value: 30000, label: 'Every 30s' },
            { value: 60000, label: 'Every 60s' },
          ]}
        />
      </PageHeader>

      {(error || usageError || statusMsg || quotaBypassNotice) ? (
        <div className="dashboardAlerts">
          {error ? <div className="alert alert-error">{error}</div> : null}
          {usageError ? <div className="alert alert-warning">{usageError}</div> : null}
          {statusMsg ? <div className="alert alert-warning">{statusMsg}</div> : null}
          {quotaBypassNotice ? <div className="alert alert-info">{quotaBypassNotice}</div> : null}
        </div>
      ) : null}

      <AdminPageSections className="dashboardPanels">
        {canViewActivity ? (
          <RecentActivityCollapsibleSection
            storageKey="customer-recent-activity"
            storageScope="customer-dashboard"
            limit={5}
            refreshSignal={autoRefresh.lastUpdatedAt}
          />
        ) : null}

        <CollapsibleCard storageKey="customer-overview" storageScope="customer-dashboard" title="Overview" defaultExpanded>
          {initialLoading && !summary ? (
            <div className="loading-inline" role="status">
              <span className="spinner" aria-hidden />
              Loading…
            </div>
          ) : cards ? (
            <div className="metric-chips metric-chips--wrap dashboard-metric-grid">
              <MetricChip label="Active searches" value={cards.enabled_searches} />
              <MetricChip label="Recipients" value={cards.enabled_recipients} />
              <MetricChip label="Matches (24h)" value={cards.new_links_24h} />
              <MetricChip label="Matches today" value={cards.new_links_today} />
              <MetricChip label="Notifications sent (24h)" value={cards.send_success_24h} variant="success" />
              <MetricChip
                label="Notification failures (24h)"
                value={cards.send_failure_24h}
                variant={cards.send_failure_24h ? 'danger' : 'success'}
              />
            </div>
          ) : (
            <p className="muted">No overview data yet.</p>
          )}
        </CollapsibleCard>

        {canViewActivity ? (
          <AttentionCollapsibleSection
            storageKey="customer-attention"
            storageScope="customer-dashboard"
            refreshSignal={autoRefresh.lastUpdatedAt}
          />
        ) : null}

        <CollapsibleCard storageKey="customer-usage" storageScope="customer-dashboard" title="Plan & usage" defaultExpanded={false}>
          {usage ? (
            <div className="usage-summary-grid">
              <p>
                Plan: <strong>{planLabel}</strong>
              </p>
              <p className="muted">
                Searches: {usage.usage.activeSearches}
                {usage.limits.maxSearches != null ? ` / ${usage.limits.maxSearches}` : ' / Unlimited'}
              </p>
              <p className="muted">
                Recipients: {usage.usage.recipients}
                {usage.limits.maxNotificationRecipients != null
                  ? ` / ${usage.limits.maxNotificationRecipients}`
                  : ' / Unlimited'}
              </p>
              <p className="muted">
                Notifications today: {usage.usage.notificationsSentToday}
                {usage.limits.maxNotificationsPerDay != null
                  ? ` / ${usage.limits.maxNotificationsPerDay}`
                  : ''}
              </p>
              <div className="table-action-bar">
                <Link className="btn btn-sm" to="/usage">
                  View usage
                </Link>
                {me?.permissions.canViewCustomerBilling ? (
                  <Link className="btn btn-sm btn-primary" to="/billing">
                    Billing
                  </Link>
                ) : me?.permissions.canUseBillingCheckout || me?.permissions.canManageOrgSubscription ? (
                  <Link className="btn btn-sm btn-primary" to="/usage#manage-subscription">
                    Manage subscription
                  </Link>
                ) : null}
              </div>
            </div>
          ) : (
            <p className="muted">{usageError ?? 'Usage is not available yet.'}</p>
          )}
          <UpgradePlanSection
            billing={billing}
            onRefresh={async () => {
              const b = await api.billing.status().catch(() => null);
              setBilling(b);
            }}
          />
        </CollapsibleCard>

        {summary?.recent_discoveries?.length ? (
          <CollapsibleCard storageKey="customer-matches" storageScope="customer-dashboard" title="Recent matches" defaultExpanded={false}>
            <ul className="dashboard-recent-list">
              {summary.recent_discoveries.slice(0, 8).map((item) => (
                <li key={item.id}>
                  <a href={item.url} target="_blank" rel="noreferrer">
                    {item.title || item.url}
                  </a>
                  <span className="muted">
                    {' '}
                    · {item.source_name} · {item.discovered_at ? new Date(item.discovered_at).toLocaleString() : '—'}
                  </span>
                </li>
              ))}
            </ul>
          </CollapsibleCard>
        ) : null}
      </AdminPageSections>
    </div>
  );
}
